import React, { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import clsx from 'clsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { Column16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { SelectDropdown, Tooltip } from '@makyoapp/makyo-ui'

import { ADVANCED_COLUMN_TYPES } from '@/core/db/table'
import { BASIC_COLUMN_TYPES, BasicColumnTypeInfo, useCreateColumnContext } from '@/context/CreateColumnContext'
import { colTypeToLabel } from '@/shared/ag-grid'
import { AI_COLUMNS } from '@/shared/ag-grid-constants'
import { ColumnIconMap } from '@/shared/icons/column'
import { Info } from '@/components/Info'

import { useGridV2Context } from '../../contexts/GridV2Context'
import { Fld, Lbl } from './shared'

interface FieldColumnTypeProps {
  /**
   * portal the menu with React.createPortal
   */
  portal?: boolean
  /**
   * is using custom size
   */
  isCustomSize?: boolean
  /**
   * is in edit
   */
  isEdit?: boolean
}

/**
 * handle create column type field
 */
export function FieldColumnType({ portal = true, isCustomSize, isEdit = false }: FieldColumnTypeProps) {
  const { activeTable } = useGridV2Context()
  const { id, value, setValue } = useCreateColumnContext()
  const [initialType] = useState(value.type)

  const ty = BASIC_COLUMN_TYPES.find((bct) => bct.type === value.type)

  const columnOptions = useMemo(() => {
    let filteredOptions = BASIC_COLUMN_TYPES.filter(
      (ct) =>
        ![
          ColumnTypes.Join,
          ColumnTypes.RowID,
          ColumnTypes.Emoji,
          ColumnTypes.MultiSelect,
          ColumnTypes.AISEO,
          ColumnTypes.AITextProcessing,
        ].includes(ct.type)
    )

    // if (activeTable?.isQuery) {
    //   filteredOptions = filteredOptions.filter((ct) =>
    //     [
    //       ColumnTypes.Connection,
    //       ColumnTypes.Lookup,
    //       ColumnTypes.Function,
    //       ColumnTypes.Math,
    //       ColumnTypes.Template,
    //       ColumnTypes.SingleCell,
    //     ].includes(ct.type)
    //   )

    //   // added placeholder option
    //   filteredOptions.unshift({
    //     type: '' as any,
    //     name: 'Select Column',
    //     description: '',
    //     keywords: [],
    //     isPlaceholder: true,
    //   })
    // }

    const standardColumns = filteredOptions.filter((ct) => !ADVANCED_COLUMN_TYPES.includes(ct.type))
    const advancedColumns = filteredOptions.filter((ct) => ADVANCED_COLUMN_TYPES.includes(ct.type))

    return [
      '#{Standard}#',
      '#div#',
      ...standardColumns,
      '#div#',
      '#{Advanced}#',
      '#div#',
      ...advancedColumns,
    ] as BasicColumnTypeInfo[]
    //
  }, [])

  const isSourceStandardColumn = activeTable?.isSource && !ADVANCED_COLUMN_TYPES.includes(value.type)
  const isSourceAdvancedColumn = activeTable?.isSource && ADVANCED_COLUMN_TYPES.includes(value.type)
  const isQueryStandardColumn = activeTable?.isQuery && !ADVANCED_COLUMN_TYPES.includes(value.type)

  return (
    <Fld className='relative'>
      <Lbl>
        <Trans>Column Type</Trans>
      </Lbl>
      <div className='flex flex-col gap-2 w-full'>
        <SelectDropdown
          value={ty}
          id={`${id}-column-type`}
          menuContainerClassName='mk-ag-sdd'
          menuZIndex='z-305'
          onChange={(option, e) => {
            setValue({
              type: option.type,
            })
          }}
          portalMenu={portal}
          menuClassName={clsx('w-[245px] [&_.sdd-heading]:text-light', isCustomSize && 'max-h-[200px]!')}
          withSearch={true}
          optionLabel={(opt: BasicColumnTypeInfo) => {
            return `${colTypeToLabel(opt.type)} ${opt.keywords.join(' ')}`
          }}
          enableAutoScroll
          renderOption={(opt: BasicColumnTypeInfo) => {
            if (opt.isPlaceholder) {
              return (
                <div className='flex-c4 py-[1px] text-light'>
                  <Column16 />
                  <div className='flex-c'>
                    <div>Select Column</div>
                  </div>
                </div>
              )
            }
            const isAI = AI_COLUMNS.includes(opt.type)

            return (
              <div className='flex-c4 py-[1px]'>
                <MaterialIcon icon={ColumnIconMap[opt.type]} size={16} />
                <div className='flex-c'>
                  <div>{colTypeToLabel(opt.type)}</div>
                  {isAI && <div className='mk-ai-badge'>AI</div>}
                </div>
              </div>
            )
          }}
          options={columnOptions}
        />
        {isEdit && initialType !== `${value.type}` && (
          <Info className='red text-m-sm'>
            <Trans>Changing the field type could delete this field’s data in some records.</Trans>
          </Info>
        )}
        {isSourceStandardColumn && (
          <div className='text-m-sm text-light flex gap-1'>
            Standard columns will be automatically added to all derived query and snapshot tables.
          </div>
        )}
        {isSourceAdvancedColumn && (
          <div className='text-m-sm text-light flex gap-1'>
            Advanced columns will automatically be added to all derived snapshot tables.
          </div>
        )}
        {isQueryStandardColumn && (
          <div className='text-m-sm text-light'>
            <span>Standard columns</span> will be added to the source table. To add a column that only appears in this
            query view, please select an &nbsp;
            <Tooltip
              content={
                <div className='max-w-[200px] text-m-s'>
                  <div>Advanced column includes:</div>
                  <ul className='mt-1 list-inside'>
                    {ADVANCED_COLUMN_TYPES.map((type) => (
                      <li key={type}>- {colTypeToLabel(type)}</li>
                    ))}
                  </ul>
                </div>
              }
            >
              <span className='cursor-pointer text-brand-500'>advanced column</span>
            </Tooltip>
            &nbsp;type.
          </div>
        )}
      </div>
    </Fld>
  )
}
